import { cloneElement, isValidElement, useCallback, useLayoutEffect, useRef, useState } from 'react'
import { resolveComponentVariant } from '../componentContract'
import { OverlayPortal, useOverlayLifecycle } from './OverlayFoundation'
import styles from './Overlays.module.css'
import { useDismissOnOutside } from '../../../hooks/useDismissOnOutside'

const placements = ['bottom-start', 'bottom-end', 'top-start', 'top-end']

export function Popover({
  trigger,
  children,
  open,
  defaultOpen = false,
  onOpenChange,
  placement = 'bottom-end',
  label = 'Popover',
  className = '',
  contentClassName = '',
  anchorRef,
  preserveFocus = false,
  role = 'dialog',
  // The surface's id, for a trigger's aria-controls.
  id,
  // Extra inline style for the surface (a width, say); its position wins.
  style,
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isOpen = open ?? internalOpen
  const triggerRef = useRef(null)
  // What the surface sits against: anchorRef when given, else the trigger.
  const anchorElementRef = anchorRef || triggerRef
  const surfaceRef = useRef(null)
  const preserveFocusRef = useRef(Boolean(preserveFocus))
  preserveFocusRef.current = preserveFocus
  const [position, setPosition] = useState(null)
  const resolvedPlacement = resolveComponentVariant('Popover placement', placement, placements, 'bottom-end')
  const setOpen = useCallback((next) => {
    if (open === undefined) setInternalOpen(next)
    onOpenChange?.(next)
  }, [onOpenChange, open])
  useOverlayLifecycle({
    open: isOpen,
    onClose: () => setOpen(false),
    dismissable: true,
    modal: false,
    trapFocus: false,
    surfaceRef,
    returnFocusRef: anchorElementRef,
    preserveFocusRef,
  })

  useLayoutEffect(() => {
    if (!isOpen) return undefined
    const update = () => {
      const rect = anchorElementRef.current?.getBoundingClientRect()
      if (!rect) return
      const gap = 6
      const margin = 8
      // A trigger near the bottom of a short viewport used to render the
      // surface below the fold, where it could not be reached at all. Flip to
      // whichever side actually has room, preferring the requested placement.
      const surfaceHeight = surfaceRef.current?.offsetHeight || 0
      const fitsBelow = rect.bottom + gap + surfaceHeight <= window.innerHeight - margin
      const fitsAbove = rect.top - gap - surfaceHeight >= margin
      const wantsAbove = resolvedPlacement.startsWith('top')
      const above = wantsAbove ? (fitsAbove || !fitsBelow) : (!fitsBelow && fitsAbove)
      const desiredTop = above ? rect.top - gap - surfaceHeight : rect.bottom + gap
      const top = Math.max(margin, Math.min(desiredTop, window.innerHeight - margin - surfaceHeight))
      const width = surfaceRef.current?.offsetWidth || 0
      const desiredLeft = resolvedPlacement.endsWith('start') ? rect.left : rect.right - width
      const left = Math.max(margin, Math.min(desiredLeft, window.innerWidth - margin - width))
      const style = { top, left }
      setPosition(style)
    }
    update()
    const observer = new ResizeObserver(update)
    if (surfaceRef.current) observer.observe(surfaceRef.current)
    if (anchorRef?.current) observer.observe(anchorRef.current)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => { observer.disconnect(); window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true) }
  }, [anchorRef, anchorElementRef, isOpen, resolvedPlacement])

  useDismissOnOutside([surfaceRef, anchorElementRef], () => setOpen(false), { enabled: isOpen })

  const triggerNode = anchorRef
    ? null
    : (isValidElement(trigger) ? cloneElement(trigger, {
      ref: (node) => {
        triggerRef.current = node
        const originalRef = trigger.ref
        if (typeof originalRef === 'function') originalRef(node)
        else if (originalRef) originalRef.current = node
      },
      'aria-haspopup': trigger.props['aria-haspopup'] || 'dialog',
      'aria-expanded': isOpen,
      onClick: (event) => { trigger.props.onClick?.(event); setOpen(!isOpen) },
    }) : trigger)

  return (
    <>
      {triggerNode}
      {isOpen ? (
        <OverlayPortal>
          <div
            ref={surfaceRef}
            id={id}
            className={`${styles.popover} ${className}`.trim()}
            style={style || position ? { ...style, ...position } : undefined}
            role={role}
            aria-label={role ? label : undefined}
            tabIndex={-1}
          >
            <div className={`${styles.popoverContent} ${contentClassName}`.trim()}>{children}</div>
          </div>
        </OverlayPortal>
      ) : null}
    </>
  )
}
